import React, { useEffect, useMemo, useRef } from 'react';
import { Form, Tab, Tabs } from 'react-bootstrap';
import { ResourceFormPage } from '../../components/resource/ResourceFormPage';
import { EtiquetaPreview, EtiquetaPreviewHandle } from '../../components/etiquetas/EtiquetaPreview';
import { etiquetasModelosService } from '../../services';
import { EtiquetaModelo, TipoImpressora } from '../../types/etiquetas';

type LayoutElement = {
  x_inicio: number;
  y_inicio: number;
  x_fim: number;
  y_fim: number;
  ativo?: boolean;
  fonte_tamanho_base?: number;
  fonte_negrito?: boolean;
  centralizado?: boolean;
  valores?: {
    x_offset_percentual?: number;
    fonte_tamanho_base?: number;
    fonte_negrito?: boolean;
  };
  mostrar_label?: boolean;
  mostrar_hri?: boolean;
};

type LayoutConfig = Record<string, LayoutElement>;

const DEFAULT_LAYOUT_CONFIG: LayoutConfig = {
  logo: {
    x_inicio: 1,
    y_inicio: 1,
    x_fim: 24,
    y_fim: 24,
    ativo: true,
  },
  codigo_material: {
    x_inicio: 8,
    y_inicio: 1,
    x_fim: 97,
    y_fim: 22,
    fonte_tamanho_base: 48,
    fonte_negrito: true,
    centralizado: true,
    ativo: true,
  },
  descricao_produto: {
    x_inicio: 2,
    y_inicio: 24,
    x_fim: 72,
    y_fim: 38,
    fonte_tamanho_base: 16,
    ativo: true,
  },
  quantidade_info: {
    x_inicio: 2,
    y_inicio: 37,
    x_fim: 90,
    y_fim: 40,
    fonte_tamanho_base: 12,
    ativo: true,
    valores: {
      x_offset_percentual: 18,
      fonte_tamanho_base: 9,
      fonte_negrito: false,
    },
  },
  batch_info: {
    x_inicio: 2,
    y_inicio: 39,
    x_fim: 90,
    y_fim: 55,
    fonte_tamanho_base: 12,
    ativo: true,
    valores: {
      x_offset_percentual: 18,
      fonte_tamanho_base: 9,
      fonte_negrito: false,
    },
  },
  pallet_info: {
    x_inicio: 2,
    y_inicio: 50,
    x_fim: 90,
    y_fim: 60,
    fonte_tamanho_base: 12,
    ativo: true,
    valores: {
      x_offset_percentual: 18,
      fonte_tamanho_base: 9,
      fonte_negrito: false,
    },
  },
  barcode_vertical: {
    x_inicio: 70,
    y_inicio: 10,
    x_fim: 105,
    y_fim: 65,
    mostrar_label: true,
    mostrar_hri: true,
    ativo: true,
  },
  barcode_horizontal: {
    x_inicio: 3,
    y_inicio: 64,
    x_fim: 97,
    y_fim: 100,
    mostrar_label: true,
    mostrar_hri: false,
    ativo: true,
  },
};

const splitQuantBlocks = (cfg: LayoutElement) => {
  const yStart = Math.min(cfg.y_inicio, cfg.y_fim);
  const yEnd = Math.max(cfg.y_inicio, cfg.y_fim);
  const lineHeight = (yEnd - yStart) / 3 || 0;
  const base = {
    ...cfg,
    valores: {
      ...(cfg.valores || {}),
    },
  };
  return {
    quantidade_info: {
      ...base,
      y_inicio: yStart,
      y_fim: yStart + lineHeight,
    },
    batch_info: {
      ...base,
      y_inicio: yStart + lineHeight,
      y_fim: yStart + lineHeight * 2,
    },
    pallet_info: {
      ...base,
      y_inicio: yStart + lineHeight * 2,
      y_fim: yEnd,
    },
  };
};

const normalizeLayoutConfig = (raw?: Record<string, any> | null): LayoutConfig => {
  const rawElements = raw && typeof raw === 'object' && 'elementos' in raw ? raw.elementos : raw;
  const elements = rawElements && typeof rawElements === 'object' ? rawElements : {};
  const merged: LayoutConfig = {};
  const withSplit = { ...elements };
  if (
    elements.dados_quantitativos &&
    !elements.quantidade_info &&
    !elements.batch_info &&
    !elements.pallet_info
  ) {
    Object.assign(withSplit, splitQuantBlocks(elements.dados_quantitativos));
  }

  Object.keys(DEFAULT_LAYOUT_CONFIG).forEach((key) => {
    const base = DEFAULT_LAYOUT_CONFIG[key];
    const override = withSplit[key] || {};
    merged[key] = {
      ...base,
      ...override,
      valores: {
        ...(base.valores || {}),
        ...((override && override.valores) || {}),
      },
    };
  });

  Object.keys(withSplit).forEach((key) => {
    if (!merged[key]) {
      merged[key] = withSplit[key];
    }
  });

  return merged;
};

const parseNumber = (value: unknown, fallback = 0) => {
  if (value === '' || value === undefined || value === null) {
    return fallback;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const isEnabled = (value: unknown, defaultValue = true) => {
  if (value === undefined || value === null) {
    return defaultValue;
  }
  if (value === false || value === 0 || value === '0' || value === 'false') {
    return false;
  }
  return true;
};

const clampLayoutBounds = (raw: unknown) => {
  if (!raw || typeof raw !== 'object') {
    return raw;
  }
  const hasElementos = 'elementos' in raw && typeof (raw as Record<string, unknown>).elementos === 'object';
  const elements = (hasElementos ? (raw as Record<string, any>).elementos : raw) as Record<
    string,
    any
  >;
  const nextElements: Record<string, any> = {};

  Object.entries(elements).forEach(([key, value]) => {
    if (!value || typeof value !== 'object') {
      nextElements[key] = value;
      return;
    }
    const nextValue = { ...value };
    if (typeof nextValue.x_inicio === 'number' && typeof nextValue.x_fim === 'number') {
      if (nextValue.x_fim < nextValue.x_inicio) {
        nextValue.x_fim = nextValue.x_inicio;
      }
    }
    if (typeof nextValue.y_inicio === 'number' && typeof nextValue.y_fim === 'number') {
      if (nextValue.y_fim < nextValue.y_inicio) {
        nextValue.y_fim = nextValue.y_inicio;
      }
    }
    nextElements[key] = nextValue;
  });

  if (hasElementos) {
    return { ...(raw as Record<string, unknown>), elementos: nextElements };
  }
  return nextElements;
};

const LayoutConfigEditor: React.FC<{
  value: unknown;
  onChange: (value: unknown) => void;
  logoValues: {
    largura: unknown;
    altura: unknown;
    padding: unknown;
  };
  onLogoChange: (field: 'logo_largura_mm' | 'logo_altura_mm' | 'logo_padding_mm', value: unknown) => void;
}> = ({ value, onChange, logoValues, onLogoChange }) => {
  const config = useMemo(() => normalizeLayoutConfig(value as Record<string, any> | null), [value]);

  useEffect(() => {
    if (!value || typeof value !== 'object') {
      onChange(config);
    }
  }, [value, config, onChange]);

  const updateField = (section: keyof LayoutConfig, key: string, rawValue: unknown) => {
    const sectionConfig = config[section] || {};
    const previousValue =
      typeof (sectionConfig as Record<string, unknown>)[key] === 'number'
        ? (sectionConfig as Record<string, unknown>)[key]
        : undefined;
    const parsedValue =
      typeof rawValue === 'boolean'
        ? rawValue
        : parseNumber(rawValue, (previousValue as number | undefined) ?? 0);

    const nextSection = {
      ...sectionConfig,
      [key]: parsedValue,
    };
    onChange({ ...config, [section]: nextSection });
  };

  const updateNested = (
    section: keyof LayoutConfig,
    key: string,
    rawValue: unknown,
  ) => {
    const sectionConfig = config[section] || {};
    const previousValue =
      typeof (sectionConfig.valores as Record<string, unknown> | undefined)?.[key] === 'number'
        ? (sectionConfig.valores as Record<string, unknown> | undefined)?.[key]
        : undefined;
    const nextValues = {
      ...(sectionConfig.valores || {}),
      [key]: parseNumber(rawValue, (previousValue as number | undefined) ?? 0),
    };
    const nextSection = {
      ...sectionConfig,
      valores: nextValues,
    };
    onChange({ ...config, [section]: nextSection });
  };

  const renderNumberInput = (
    label: string,
    value: unknown,
    onValueChange: (rawValue: unknown) => void,
  ) => (
    <Form.Group>
      <Form.Label className="small text-muted">{label}</Form.Label>
      <Form.Control
        type="number"
        step="1"
        value={value === null || value === undefined ? '' : (value as string | number)}
        onChange={(event) => onValueChange(event.target.value)}
      />
    </Form.Group>
  );

  const renderCheckbox = (
    label: string,
    checked: boolean,
    onValueChange: (nextValue: boolean) => void,
  ) => (
    <Form.Check
      type="switch"
      label={label}
      checked={checked}
      onChange={(event) => onValueChange(event.target.checked)}
    />
  );

  const renderBlock = (title: string, content: React.ReactNode) => (
    <div className="border rounded-2 p-3 bg-light d-flex flex-column gap-3">
      <div className="fw-semibold">{title}</div>
      {content}
    </div>
  );

  const renderInfoBlock = (title: string, section: keyof LayoutConfig) => {
    const cfg = (config[section] ?? {}) as LayoutElement;
    return renderBlock(
      title,
      <>
        {renderCheckbox('Ativo', isEnabled(cfg.ativo, true), (val) =>
          updateField(section, 'ativo', val),
        )}
        <div className="row g-2">
          <div className="col-12 col-md-6 col-lg-3">
            {renderNumberInput('X Inicio', cfg.x_inicio, (val) =>
              updateField(section, 'x_inicio', val),
            )}
          </div>
          <div className="col-12 col-md-6 col-lg-3">
            {renderNumberInput('X Fim', cfg.x_fim, (val) =>
              updateField(section, 'x_fim', val),
            )}
          </div>
          <div className="col-12 col-md-6 col-lg-3">
            {renderNumberInput('Y Inicio', cfg.y_inicio, (val) =>
              updateField(section, 'y_inicio', val),
            )}
          </div>
          <div className="col-12 col-md-6 col-lg-3">
            {renderNumberInput('Y Fim', cfg.y_fim, (val) =>
              updateField(section, 'y_fim', val),
            )}
          </div>
        </div>
        <div className="row g-2">
          <div className="col-12 col-md-6 col-lg-4">
            {renderNumberInput('Fonte Tamanho', cfg.fonte_tamanho_base, (val) =>
              updateField(section, 'fonte_tamanho_base', val),
            )}
          </div>
          <div className="col-12 col-md-6 col-lg-4">
            {renderNumberInput('Offset Valores (%)', cfg.valores?.x_offset_percentual, (val) =>
              updateNested(section, 'x_offset_percentual', val),
            )}
          </div>
        </div>
      </>,
    );
  };

  return (
    <Tabs defaultActiveKey="logo" className="mb-3">
      <Tab eventKey="logo" title="Logotipo">
        {renderBlock(
          'Logotipo da Empresa',
          <>
          {renderCheckbox(
            'Ativo',
            isEnabled(config.logo?.ativo, true),
            (val) => updateField('logo', 'ativo', val),
          )}
            <div className="row g-2">
              <div className="col-12 col-md-6 col-lg-3">
                {renderNumberInput('X Inicio', config.logo?.x_inicio, (val) =>
                  updateField('logo', 'x_inicio', val),
                )}
              </div>
              <div className="col-12 col-md-6 col-lg-3">
                {renderNumberInput('X Fim', config.logo?.x_fim, (val) =>
                  updateField('logo', 'x_fim', val),
                )}
              </div>
              <div className="col-12 col-md-6 col-lg-3">
                {renderNumberInput('Y Inicio', config.logo?.y_inicio, (val) =>
                  updateField('logo', 'y_inicio', val),
                )}
              </div>
              <div className="col-12 col-md-6 col-lg-3">
                {renderNumberInput('Y Fim', config.logo?.y_fim, (val) =>
                  updateField('logo', 'y_fim', val),
                )}
              </div>
            </div>
            <div className="row g-2">
              <div className="col-12 col-md-6 col-lg-4">
                {renderNumberInput('Logo Largura (mm)', logoValues.largura, (val) =>
                  onLogoChange('logo_largura_mm', val),
                )}
              </div>
              <div className="col-12 col-md-6 col-lg-4">
                {renderNumberInput('Logo Altura (mm)', logoValues.altura, (val) =>
                  onLogoChange('logo_altura_mm', val),
                )}
              </div>
              <div className="col-12 col-md-6 col-lg-4">
                {renderNumberInput('Logo Padding (mm)', logoValues.padding, (val) =>
                  onLogoChange('logo_padding_mm', val),
                )}
              </div>
            </div>
          </>,
        )}
      </Tab>
      <Tab eventKey="codigo" title="Codigo Material">
        {renderBlock(
          'Codigo Principal do Material',
          <>
          {renderCheckbox(
            'Ativo',
            isEnabled(config.codigo_material?.ativo, true),
            (val) => updateField('codigo_material', 'ativo', val),
          )}
            <div className="row g-2">
              <div className="col-12 col-md-6 col-lg-3">
                {renderNumberInput('X Inicio', config.codigo_material?.x_inicio, (val) =>
                  updateField('codigo_material', 'x_inicio', val),
                )}
              </div>
              <div className="col-12 col-md-6 col-lg-3">
                {renderNumberInput('X Fim', config.codigo_material?.x_fim, (val) =>
                  updateField('codigo_material', 'x_fim', val),
                )}
              </div>
              <div className="col-12 col-md-6 col-lg-3">
                {renderNumberInput('Y Inicio', config.codigo_material?.y_inicio, (val) =>
                  updateField('codigo_material', 'y_inicio', val),
                )}
              </div>
              <div className="col-12 col-md-6 col-lg-3">
                {renderNumberInput('Y Fim', config.codigo_material?.y_fim, (val) =>
                  updateField('codigo_material', 'y_fim', val),
                )}
              </div>
            </div>
            <div className="row g-2">
              <div className="col-12 col-md-6 col-lg-4">
                {renderNumberInput(
                  'Fonte Tamanho',
                  config.codigo_material?.fonte_tamanho_base,
                  (val) => updateField('codigo_material', 'fonte_tamanho_base', val),
                )}
              </div>
              <div className="col-12 col-md-6 col-lg-4 d-flex align-items-end">
              {renderCheckbox(
                'Centralizado',
                isEnabled(config.codigo_material?.centralizado, true),
                (val) => updateField('codigo_material', 'centralizado', val),
              )}
              </div>
              <div className="col-12 col-md-6 col-lg-4 d-flex align-items-end">
              {renderCheckbox(
                'Negrito',
                isEnabled(config.codigo_material?.fonte_negrito, true),
                (val) => updateField('codigo_material', 'fonte_negrito', val),
              )}
              </div>
            </div>
          </>,
        )}
      </Tab>
      <Tab eventKey="descricao" title="Descricao">
        {renderBlock(
          'Descricao do Produto',
          <>
          {renderCheckbox(
            'Ativo',
            isEnabled(config.descricao_produto?.ativo, true),
            (val) => updateField('descricao_produto', 'ativo', val),
          )}
            <div className="row g-2">
              <div className="col-12 col-md-6 col-lg-3">
                {renderNumberInput('X Inicio', config.descricao_produto?.x_inicio, (val) =>
                  updateField('descricao_produto', 'x_inicio', val),
                )}
              </div>
              <div className="col-12 col-md-6 col-lg-3">
                {renderNumberInput('X Fim', config.descricao_produto?.x_fim, (val) =>
                  updateField('descricao_produto', 'x_fim', val),
                )}
              </div>
              <div className="col-12 col-md-6 col-lg-3">
                {renderNumberInput('Y Inicio', config.descricao_produto?.y_inicio, (val) =>
                  updateField('descricao_produto', 'y_inicio', val),
                )}
              </div>
              <div className="col-12 col-md-6 col-lg-3">
                {renderNumberInput('Y Fim', config.descricao_produto?.y_fim, (val) =>
                  updateField('descricao_produto', 'y_fim', val),
                )}
              </div>
            </div>
            <div className="row g-2">
              <div className="col-12 col-md-6 col-lg-4">
                {renderNumberInput(
                  'Fonte Tamanho',
                  config.descricao_produto?.fonte_tamanho_base,
                  (val) => updateField('descricao_produto', 'fonte_tamanho_base', val),
                )}
              </div>
            </div>
          </>,
        )}
      </Tab>
      <Tab eventKey="quantidade" title="Quantity">
        {renderInfoBlock('Quantity', 'quantidade_info')}
      </Tab>
      <Tab eventKey="batch" title="Batch">
        {renderInfoBlock('Batch', 'batch_info')}
      </Tab>
      <Tab eventKey="pallet" title="Pallet no.">
        {renderInfoBlock('Pallet no.', 'pallet_info')}
      </Tab>
      <Tab eventKey="barcode-vertical" title="Barcode Vertical">
        {renderBlock(
          'Codigo de Barras Vertical (Unit Identifier)',
          <>
          {renderCheckbox(
            'Ativo',
            isEnabled(config.barcode_vertical?.ativo, true),
            (val) => updateField('barcode_vertical', 'ativo', val),
          )}
            <div className="row g-2">
              <div className="col-12 col-md-6 col-lg-3">
                {renderNumberInput('X Inicio', config.barcode_vertical?.x_inicio, (val) =>
                  updateField('barcode_vertical', 'x_inicio', val),
                )}
              </div>
              <div className="col-12 col-md-6 col-lg-3">
                {renderNumberInput('X Fim', config.barcode_vertical?.x_fim, (val) =>
                  updateField('barcode_vertical', 'x_fim', val),
                )}
              </div>
              <div className="col-12 col-md-6 col-lg-3">
                {renderNumberInput('Y Inicio', config.barcode_vertical?.y_inicio, (val) =>
                  updateField('barcode_vertical', 'y_inicio', val),
                )}
              </div>
              <div className="col-12 col-md-6 col-lg-3">
                {renderNumberInput('Y Fim', config.barcode_vertical?.y_fim, (val) =>
                  updateField('barcode_vertical', 'y_fim', val),
                )}
              </div>
            </div>
            <div className="row g-2">
              <div className="col-12 col-md-6 col-lg-4 d-flex align-items-end">
              {renderCheckbox(
                'Mostrar Label',
                isEnabled(config.barcode_vertical?.mostrar_label, true),
                (val) => updateField('barcode_vertical', 'mostrar_label', val),
              )}
              </div>
              <div className="col-12 col-md-6 col-lg-4 d-flex align-items-end">
              {renderCheckbox(
                'Mostrar HRI',
                isEnabled(config.barcode_vertical?.mostrar_hri, true),
                (val) => updateField('barcode_vertical', 'mostrar_hri', val),
              )}
              </div>
            </div>
          </>,
        )}
      </Tab>
      <Tab eventKey="barcode-horizontal" title="Barcode Principal">
        {renderBlock(
          'Codigo de Barras Principal (Quant Data)',
          <>
          {renderCheckbox(
            'Ativo',
            isEnabled(config.barcode_horizontal?.ativo, true),
            (val) => updateField('barcode_horizontal', 'ativo', val),
          )}
            <div className="row g-2">
              <div className="col-12 col-md-6 col-lg-3">
                {renderNumberInput('X Inicio', config.barcode_horizontal?.x_inicio, (val) =>
                  updateField('barcode_horizontal', 'x_inicio', val),
                )}
              </div>
              <div className="col-12 col-md-6 col-lg-3">
                {renderNumberInput('X Fim', config.barcode_horizontal?.x_fim, (val) =>
                  updateField('barcode_horizontal', 'x_fim', val),
                )}
              </div>
              <div className="col-12 col-md-6 col-lg-3">
                {renderNumberInput('Y Inicio', config.barcode_horizontal?.y_inicio, (val) =>
                  updateField('barcode_horizontal', 'y_inicio', val),
                )}
              </div>
              <div className="col-12 col-md-6 col-lg-3">
                {renderNumberInput('Y Fim', config.barcode_horizontal?.y_fim, (val) =>
                  updateField('barcode_horizontal', 'y_fim', val),
                )}
              </div>
            </div>
            <div className="row g-2">
              <div className="col-12 col-md-6 col-lg-4 d-flex align-items-end">
              {renderCheckbox(
                'Mostrar Label',
                isEnabled(config.barcode_horizontal?.mostrar_label, true),
                (val) => updateField('barcode_horizontal', 'mostrar_label', val),
              )}
              </div>
              <div className="col-12 col-md-6 col-lg-4 d-flex align-items-end">
              {renderCheckbox(
                'Mostrar HRI',
                isEnabled(config.barcode_horizontal?.mostrar_hri, false),
                (val) => updateField('barcode_horizontal', 'mostrar_hri', val),
              )}
              </div>
            </div>
          </>,
        )}
      </Tab>
    </Tabs>
  );
};

export const EtiquetasModelosFormPage: React.FC = () => {
const buildPreviewPayload = (formData: Record<string, unknown>) => {
  const toNumber = (value: unknown, fallback: number) => {
    if (value === '' || value === undefined || value === null) {
      return fallback;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };
  const toStringValue = (value: unknown, fallback: string) =>
    value === undefined || value === null || value === '' ? fallback : String(value);

  const largura = toNumber(formData.largura_mm, 100);
  const altura = toNumber(formData.altura_mm, 50);
  const tipo: TipoImpressora =
    formData.tipo_impressora === 'pimaco' ? 'pimaco' : 'zebra';

  const layoutConfig = normalizeLayoutConfig(
    (formData.layout_config as Record<string, any> | null) ?? null,
  );

  const modeloPreview: EtiquetaModelo = {
    id: 0,
    nome: toStringValue(formData.nome, 'Modelo de Etiqueta'),
    descricao: toStringValue(formData.descricao, ''),
    tipo_impressora: tipo,
    largura_mm: largura,
    altura_mm: altura,
    por_linha: toNumber(formData.por_linha, 1),
    linhas: toNumber(formData.linhas, 1),
    margem_esquerda_mm: toNumber(formData.margem_esquerda_mm, 0),
    margem_superior_mm: toNumber(formData.margem_superior_mm, 0),
    margem_direita_mm: toNumber(formData.margem_direita_mm, 0),
    margem_inferior_mm: toNumber(formData.margem_inferior_mm, 0),
    espaco_horizontal_mm: toNumber(formData.espaco_horizontal_mm, 0),
    espaco_vertical_mm: toNumber(formData.espaco_vertical_mm, 0),
    rotacao_graus: toNumber(formData.rotacao_graus, 0),
    familia: toStringValue(formData.familia, ''),
    layout_config: layoutConfig,
    dados_padrao: null,
    ativo:
      formData.ativo === '' || formData.ativo === undefined || formData.ativo === null
        ? true
        : Boolean(Number(formData.ativo)),
    logo_largura_mm: toNumber(formData.logo_largura_mm, 0),
    logo_altura_mm: toNumber(formData.logo_altura_mm, 0),
    logo_padding_mm: toNumber(formData.logo_padding_mm, 0),
  };

  const dadosPreview = {
    codigo_material: 'MP00002',
    descricao: 'Produto exemplo',
    quantidade: 1,
    unidade: 'PC',
    lote: '2501140201',
    numero_pallet: 1,
    codigo_tetra: '4568',
    codigo_fornecedor: '596',
    data: '2026-01-29',
    tamanho_etiqueta: `${largura}x${altura}`,
    code_mode: 'barcode',
  };

  return { modeloPreview, dadosPreview };
};

const ModeloPreviewSection: React.FC<{ formData: Record<string, unknown> }> = ({ formData }) => {
  const previewRef = useRef<EtiquetaPreviewHandle | null>(null);
  const previewPayload = useMemo(() => buildPreviewPayload(formData), [formData]);

  useEffect(() => {
    previewRef.current?.generatePreview();
  }, [previewPayload]);

  return (
    <div className="etiqueta-preview-modelo">
      <EtiquetaPreview
        ref={previewRef}
        dados={previewPayload.dadosPreview}
        modelo={previewPayload.modeloPreview}
      />
    </div>
  );
};

  return (
    <ResourceFormPage<EtiquetaModelo>
      title="Modelo de Etiqueta"
      service={etiquetasModelosService}
      fields={[
        {
          name: 'nome',
          label: 'Nome do Modelo',
          type: 'text',
          required: true,
          placeholder: 'Ex: Etiqueta Pallet Padrão',
          colClass: 'col-12',
        },
        {
          name: 'descricao',
          label: 'Descrição',
          type: 'text',
          placeholder: 'Descrição do modelo de etiqueta',
          colClass: 'col-12',
        },

        {
          name: 'largura_mm',
          label: 'Largura (mm)',
          type: 'number',
          required: true,
          placeholder: '100',
          colClass: 'col-12 col-md-6',
        },
        {
          name: 'altura_mm',
          label: 'Altura (mm)',
          type: 'number',
          required: true,
          placeholder: '50',
          colClass: 'col-12 col-md-6',
        },
        {
          name: 'espaco_horizontal_mm',
          label: 'Espaco Horizontal (mm)',
          type: 'number',
          placeholder: '0',
          colClass: 'col-12 col-md-6',
        },
        {
          name: 'espaco_vertical_mm',
          label: 'Espaco Vertical (mm)',
          type: 'number',
          placeholder: '0',
          colClass: 'col-12 col-md-6',
        },

        {
          name: 'por_linha',
          label: 'Etiquetas por Linha',
          type: 'number',
          valueType: 'number',
          hidden: true,
          fixedValue: 1,
        },
        {
          name: 'linhas',
          label: 'Numero de Linhas',
          type: 'number',
          valueType: 'number',
          hidden: true,
          fixedValue: 1,
        },
        {
          name: 'margem_esquerda_mm',
          label: 'Margem Esquerda (mm)',
          type: 'number',
          placeholder: '0',
          colClass: 'col-12 col-md-6 col-lg-3',
        },
        {
          name: 'margem_superior_mm',
          label: 'Margem Superior (mm)',
          type: 'number',
          placeholder: '0',
          colClass: 'col-12 col-md-6 col-lg-3',
        },
        {
          name: 'margem_direita_mm',
          label: 'Margem Direita (mm)',
          type: 'number',
          placeholder: '0',
          colClass: 'col-12 col-md-6 col-lg-3',
        },
        {
          name: 'margem_inferior_mm',
          label: 'Margem Inferior (mm)',
          type: 'number',
          placeholder: '0',
          colClass: 'col-12 col-md-6 col-lg-3',
        },
        {
          name: 'logo_largura_mm',
          label: 'Logo Largura (mm)',
          type: 'number',
          valueType: 'number',
          placeholder: '0',
          hidden: true,
        },
        {
          name: 'logo_altura_mm',
          label: 'Logo Altura (mm)',
          type: 'number',
          valueType: 'number',
          placeholder: '0',
          hidden: true,
        },
        {
          name: 'logo_padding_mm',
          label: 'Logo Padding (mm)',
          type: 'number',
          valueType: 'number',
          placeholder: '0',
          hidden: true,
        },
        {
          name: 'layout_config',
          label: 'Layout',
          colClass: 'col-12',
          render: ({ value, onChange, formData, setFieldValue }) => (
            <LayoutConfigEditor
              value={value}
              onChange={onChange}
              logoValues={{
                largura: formData.logo_largura_mm,
                altura: formData.logo_altura_mm,
                padding: formData.logo_padding_mm,
              }}
              onLogoChange={(field, nextValue) => {
                setFieldValue?.(field, parseNumber(nextValue, 0));
              }}
            />
          ),
        },
        {
          name: 'ativo',
          label: 'Ativo',
          type: 'select',
          options: [
            { value: 1, label: 'Sim' },
            { value: 0, label: 'Não' },
          ],
        },
      ]}
      redirectPath="/admin/producao/etiquetas-modelos"
      cancelPath="/admin/producao/etiquetas-modelos"
      renderAfterActions={(formData, _setFieldValue) => (
        <ModeloPreviewSection formData={formData} />
      )}
      transformPayload={(payload) => {
        if (!payload.layout_config) {
          return payload;
        }
        return {
          ...payload,
          layout_config: clampLayoutBounds(payload.layout_config),
        };
      }}
      onSubmitPayload={(payload) => {
        console.log('[Etiqueta] payload configs\n' + JSON.stringify(payload, null, 2));
      }}
      onPayloadChange={(payload) => {
        console.log('[Etiqueta] payload configs (on change)\n' + JSON.stringify(payload, null, 2));
      }}
    />
  );
};
